import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Leaf, Shield, Heart, Mountain, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/ProductCard';
import { MountainDivider } from '@/components/MountainDivider';
import { productos, categorias } from '@/data/products';
import { useSeo } from '@/hooks/useSeo';
import { trackWhatsappClick } from '@/utils/analytics';
import { motion } from 'framer-motion';

/** Imagen representativa por categoría (fotos del catálogo) */
const imagenPorCategoria: Record<string, string> = {
  'Frutos secos y semillas': '/placeholder-pecanas.jpg',
  'Granos y cereales': '/images/productos/avena-integral.jpg',
  'Legumbres': '/placeholder-garbanzos.jpg',
  'Harinas': '/placeholder-harina-quinua.jpg',
  'Aceites y vinagres': '/images/productos/aceite-oliva.jpg',
  'Superalimentos y endulzantes': '/images/productos/sal-maras.jpg',
};

export function HomePage() {
  useSeo();

  const handleWhatsApp = () => {
    trackWhatsappClick('home');
    window.open('https://wa.me/+51915976931?text=Hola, quiero información sobre los productos naturales de Huaytanatura', '_blank');
  };

  // Productos destacados: los más representativos del catálogo
  const featuredIds = ['pecanas', 'quinua-blanca', 'semillas-chia', 'sacha-inchi', 'harina-maca', 'kiwicha', 'almendras', 'harina-quinua'];
  const featuredProducts = featuredIds
    .map((id) => productos.find((p) => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  // Novedades: los nuevos productos de la despensa
  const novedades = productos.filter((p) =>
    ['sal-maras', 'miel-pura', 'aceite-oliva', 'mezcla-andina-7-semillas', 'polen-abeja', 'algarrobina', 'aceite-coco', 'te-verde-infusiones'].includes(p.id)
  );

  return (
    <div className="min-h-screen texture-paper">
      {/* ============ HERO ============ */}
      <section className="relative py-28 lg:py-40 overflow-hidden min-h-[85vh] flex items-center">
        {/* Imagen de fondo andino */}
        <img
          src="/images/hero-andes.jpg"
          alt="Granos andinos y montañas del Perú"
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />

        {/* Gradiente negro con opacidad para legibilidad del texto */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/55 to-black/85"></div>
        {/* Refuerzo lateral para texto en pantallas anchas */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50"></div>

        {/* Silueta de picos andinos recortando la base del hero */}
        <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
          <MountainDivider svgClassName="text-background" heightClass="h-[60px] md:h-[80px]" />
        </div>

        <div className="relative container mx-auto px-4 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-sm md:text-base uppercase tracking-[0.35em] text-natural-sage mb-4"
          >
            Ñaña - Chaclacayo · Lima Este · Perú
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
            className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            De las tierras Andinas a
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="block text-natural-cream"
            >
              la mesa de tu familia
            </motion.span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-lg lg:text-2xl text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed"
          >
            Frutos secos, granos andinos, harinas, aceites y superalimentos
            100% naturales. Alimentación consciente, directa desde productores peruanos.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button
              size="lg"
              asChild
              className="bg-card text-primary hover:bg-card/90 text-lg px-8 py-3 hover:scale-105 transition-all duration-300"
            >
              <Link to="/tienda">
                Explorar Tienda
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>

            <Button
              size="lg"
              variant="outline"
              onClick={handleWhatsApp}
              className="border-2 border-white/80 bg-black/40 text-white hover:bg-white hover:text-primary text-lg px-8 py-3 hover:scale-105 transition-all duration-300"
            >
              <MessageCircle className="mr-2 w-5 h-5" aria-hidden="true" />
              Escríbenos por WhatsApp
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-12 text-white/80 text-sm pb-8"
          >
            <p>🌿 100% naturales · 🚚 Entregas en Ñaña, Chaclacayo y Chosica · 📱 Pedidos por WhatsApp</p>
          </motion.div>
        </div>
      </section>

      {/* ============ CATEGORÍAS ============ */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Explora por categoría
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Nuestra despensa natural está organizada en {categorias.length} secciones:
              desde granos de altura hasta superalimentos y endulzantes.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5 mb-4">
            {categorias.map((categoria, index) => (
              <motion.div
                key={categoria}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Link
                  to={`/tienda?categoria=${encodeURIComponent(categoria)}`}
                  className="group block bg-card border border-border rounded-xl overflow-hidden hover:shadow-elevated hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={imagenPorCategoria[categoria] ?? '/placeholder.svg'}
                      alt={categoria}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="font-serif font-bold text-white text-sm lg:text-base leading-tight drop-shadow">
                        {categoria}
                      </p>
                      <p className="text-[10px] text-white/80 uppercase tracking-wide mt-0.5">
                        {productos.filter((p) => p.categoria === categoria).length} productos
                      </p>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRODUCTOS DESTACADOS ============ */}
      <section className="py-16 lg:py-20 bg-muted/40">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Los favoritos de la casa
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Granos, semillas y harinas que nuestras familias eligen semana a semana
              para una alimentación nutritiva y sabrosa.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (index % 4) * 0.1 }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Button size="lg" asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:scale-105 transition-all duration-300">
              <Link to="/tienda">
                Ver todos los productos
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ============ NOVEDADES ============ */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-primary font-semibold mb-3">
              <Sparkles className="w-4 h-4" />
              Recién llegados
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Novedades de nuestra despensa
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Aceites saludables, sales andinas, endulzantes naturales e infusiones:
              lo nuevo de Huaytanatura para completar tu despensa.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {novedades.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: (index % 4) * 0.1 }}
                viewport={{ once: true }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SOBRE NOSOTROS ============ */}
      <section className="py-16 lg:py-24 bg-muted/40 texture-paper">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                El espíritu de Huaytanatura
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Huaytanatura es el emprendimiento familiar de Grisly Huaytalla,
                nacido en Ñaña - Chaclacayo, en plena Carretera Central, para
                acercar los productos naturales y las harinas andinas a las
                familias de Lima Este: Ate, Ñaña, Chaclacayo y Chosica.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Trabajamos directamente con productores locales para ofrecerte
                granos, harinas, aceites y superalimentos frescos, nutritivos y de
                la mejor calidad, sin aditivos químicos ni conservantes artificiales.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Leaf, text: '100% productos naturales, sin aditivos' },
                  { icon: Shield, text: 'Seleccionados con productores locales' },
                  { icon: Mountain, text: 'Identidad andina en cada producto' },
                  { icon: Heart, text: 'Emprendimiento familiar de Lima Este' },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-center space-x-3"
                  >
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-foreground font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-card p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <h3 className="text-2xl font-bold text-foreground mb-6">¿Por qué elegirnos?</h3>
              <div className="space-y-6">
                {[
                  {
                    title: 'Calidad garantizada',
                    description: 'Seleccionamos cuidadosamente cada lote para asegurar la máxima frescura y valor nutricional.'
                  },
                  {
                    title: 'Compras responsables',
                    description: 'Apoyamos a productores locales y prácticas agrícolas sostenibles de los Andes.'
                  },
                  {
                    title: 'Asesoría personalizada',
                    description: 'Te orientamos por WhatsApp para encontrar los productos perfectos para tu familia.'
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 + 0.2 }}
                    viewport={{ once: true }}
                  >
                    <h4 className="font-semibold text-foreground mb-2">{item.title}</h4>
                    <p className="text-muted-foreground">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ BENEFICIOS ============ */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Nutrición de altura en cada producto
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Cada producto está cuidadosamente seleccionado para brindar el máximo
              valor nutricional a tu familia.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: 'Alto en fibra',
                description: 'Promueve una digestión saludable y te mantiene satisfecho por más tiempo.'
              },
              {
                icon: Leaf,
                title: 'Proteína vegetal',
                description: 'Fuentes naturales de proteínas completas para el desarrollo y mantenimiento muscular.'
              },
              {
                icon: Shield,
                title: 'Opciones sin gluten*',
                description: 'Gran selección libre de gluten para personas con sensibilidad o celiaquía. *Productos seleccionados'
              },
              {
                icon: Mountain,
                title: 'Superalimentos andinos',
                description: 'Quinua, maca, sacha inchi y más: la riqueza de los Andes en tu mesa.'
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                className="text-center p-6 bg-card rounded-xl border border-border hover:shadow-lg transition-all"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.2 }}
                  className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <benefit.icon className="w-8 h-8 text-primary" />
                </motion.div>
                <h3 className="font-bold text-foreground mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA FINAL ============ */}
      <section className="py-16 bg-primary/5 border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-3xl lg:text-4xl font-bold mb-4 text-foreground"
          >
            ¿Listo para una alimentación más saludable?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-lg mb-8 text-muted-foreground max-w-2xl mx-auto"
          >
            Explora nuestro catálogo completo de {productos.length} productos naturales
            o escríbenos por WhatsApp para recibir asesoría personalizada.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              size="lg"
              asChild
              className="bg-primary hover:bg-primary-light text-primary-foreground hover:scale-105 transition-all duration-300"
            >
              <Link to="/tienda">
                Explorar Tienda
              </Link>
            </Button>

            <Button
              size="lg"
              variant="outline"
              onClick={handleWhatsApp}
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground hover:scale-105 transition-all duration-300"
            >
              <MessageCircle className="mr-2 w-5 h-5" />
              Consultar por WhatsApp
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}